import React, { Fragment, FC, ChangeEvent, useState, useEffect } from 'react';
import {
    Box,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
} from '@mui/material';
import { ProcuredActionFilter } from 'models/action';
import { useTranslation } from 'react-i18next';
import FilterOnStatusDropdown from 'components/users/shared/filter-on-status-dropdown/filter-on-status-dropdown.component';
import ProductionTemplateFilter from '../shared/production-template-filter/production-template-filter.component';
import ActionBasedCustomersDropdown from 'components/shared/action-based-customers-dropdown/action-based-customers-dropdown.component';
import { Organization, ActionPrice } from 'models/organization';
import ClearIcon from '@mui/icons-material/Clear';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { currentOrgVar } from 'apollo/reactive-variables/organization';

type TenderedActionsPageActionsProps = {
    onFilterChange: (actionFilter: ProcuredActionFilter) => void;
    actionBasedActionList: ActionPrice[];
};
const TenderedActionsPageActions: FC<TenderedActionsPageActionsProps> = ({ onFilterChange, actionBasedActionList }) => {
    const { t: translate } = useTranslation(['lab-settings']);
    const [selectedClinic, setSelectedClinic] = useState<Organization | null>(null);
    const [uniqueCategories, setUniqueCategories] = useState<string[]>([]);
    const [category, setCategory] = useState('');
    const [actionFilter, setActionFilter] = useState<ProcuredActionFilter>({
        clinicId: '',
        freetext: '',
        category: '',
        active: true,
        productionPlanTemplate: 'ALL',
        productionValueTemplate: 'ALL',
    });

    useEffect(() => {
        if (actionBasedActionList) {
            const categories = actionBasedActionList.map((action: ActionPrice) => action.mainCategory);
            setUniqueCategories(
                categories.filter((c: string, index: number) => {
                    return categories.indexOf(c) === index;
                }),
            );
        }
    }, [actionBasedActionList]);

    const handleClinicSelectChange = (_: React.SyntheticEvent, clinic: Organization | null) => {
        setSelectedClinic(clinic);
        setActionFilter({ ...actionFilter, clinicId: clinic?._id || '' });
        onFilterChange({ ...actionFilter, clinicId: clinic?._id || '' });
    };

    const handleFreetextChange = (freetext: string) => {
        setActionFilter({ ...actionFilter, freetext: freetext });
        onFilterChange({ ...actionFilter, freetext: freetext });
    };

    const handleCategoryFilterChange = (event: SelectChangeEvent<string>) => {
        const category = uniqueCategories.find((category) => category === event.target.value);
        if (category) {
            setCategory(category);
            setActionFilter({ ...actionFilter, category: category });
            onFilterChange({ ...actionFilter, category: category });
        }
    };

    const handleClearCategory = () => {
        setCategory('');
        setActionFilter({ ...actionFilter, category: '' });
        onFilterChange({ ...actionFilter, category: '' });
    };

    const handleStatusSelect = (filterStatus: boolean | null) => {
        setActionFilter({ ...actionFilter, active: filterStatus });
        onFilterChange({ ...actionFilter, active: filterStatus });
    };

    const handleProductionPlanTemplateSelect = (productionPlanTemplate: string) => {
        setActionFilter({ ...actionFilter, productionPlanTemplate });
        onFilterChange({ ...actionFilter, productionPlanTemplate });
    };

    const handleProductionValueTemplateSelect = (productionValueTemplate: string) => {
        setActionFilter({ ...actionFilter, productionValueTemplate });
        onFilterChange({ ...actionFilter, productionValueTemplate });
    };

    return (
        <Fragment>
            <Box display="flex" alignItems="center">
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <ActionBasedCustomersDropdown
                            selectedClinic={selectedClinic}
                            onClinicFilterChange={handleClinicSelectChange}
                        />
                    </Grid>
                    {actionFilter.clinicId && (
                        <Fragment>
                            <Grid item sm={4} xs={12}>
                                <TextField
                                    fullWidth
                                    id="filter-name"
                                    margin="dense"
                                    name="search"
                                    label={translate('lab-settings:FILTER_ACTIONS')}
                                    onChange={(event: ChangeEvent<HTMLInputElement>) =>
                                        handleFreetextChange(event.target.value)
                                    }
                                />
                            </Grid>
                            <Grid item sm={4} xs={12}>
                                <FormControl margin="dense" fullWidth>
                                    <InputLabel>{translate('client-directory:MAIN_CATEGORY')}</InputLabel>
                                    <Select
                                        value={category}
                                        label={translate('client-directory:MAIN_CATEGORY')}
                                        onChange={handleCategoryFilterChange}
                                        endAdornment={
                                            <Fragment>
                                                {category !== '' && (
                                                    <IconButton
                                                        size="small"
                                                        sx={{ marginRight: '.75rem' }}
                                                        onClick={handleClearCategory}
                                                    >
                                                        <ClearIcon />
                                                    </IconButton>
                                                )}
                                            </Fragment>
                                        }
                                    >
                                        {uniqueCategories.map((category: string, index: number) => (
                                            <MenuItem key={index} value={category}>
                                                {category}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item sm={4} xs={12}>
                                <FilterOnStatusDropdown fullWidth statusSelected={handleStatusSelect} />
                            </Grid>

                            <Grid item sm={4} xs={12}>
                                <ProductionTemplateFilter
                                    templateType="plan"
                                    value={actionFilter.productionPlanTemplate}
                                    onSelect={handleProductionPlanTemplateSelect}
                                />
                            </Grid>

                            <Grid item sm={4} xs={12}>
                                <ProductionTemplateFilter
                                    templateType="value"
                                    value={actionFilter.productionValueTemplate}
                                    onSelect={handleProductionValueTemplateSelect}
                                />
                            </Grid>
                        </Fragment>
                    )}
                </Grid>
            </Box>
        </Fragment>
    );
};

export default TenderedActionsPageActions;
